import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "@clerk/react";
import { eventTracker } from "services/eventTracker";
import api from "services/api";

interface Props {
  tenantIdMap: Record<string, string>; // slug -> UUID
}

/**
 * Read user ID from Clerk's __session cookie (JWT) as a fallback
 * when useUser() hasn't resolved yet (Clerk still loading).
 */
function getUserIdFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)__session=([^;]+)/);
  if (!match) return null;
  try {
    // JWT payload is the second base64url segment
    const payload = match[1].split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json.sub || null; // Clerk stores user ID as "sub"
  } catch {
    return null;
  }
}

/** Walk up the DOM to find the closest matching ancestor (inclusive). */
function closest(el: EventTarget | null, tag: string): HTMLElement | null {
  let node = el as HTMLElement | null;
  while (node) {
    if (node.tagName === tag) return node;
    node = node.parentElement;
  }
  return null;
}

/** Build a human-readable selector string for an element. */
function elementSelector(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const classes = el.className && typeof el.className === "string"
    ? `.${el.className.trim().split(/\s+/).slice(0, 3).join(".")}`
    : "";
  return `${tag}${id}${classes}`;
}

export function EventTrackerProvider({ tenantIdMap }: Props): null {
  const { user } = useUser();
  const location = useLocation();
  const initialized = useRef(false);
  const pageEnteredAt = useRef<number>(Date.now());

  // Init tracker once on mount
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      const slugMatch = location.pathname.match(/^\/t\/([^/]+)/);
      const tenantId = slugMatch ? (tenantIdMap[slugMatch[1]] ?? null) : null;
      eventTracker.init(
        (events) => api.ingestEvents(events),
        user?.id ?? getUserIdFromCookie(),
        tenantId
      );
      eventTracker.track("session_start", { referrer: document.referrer });

      // Page load event with performance timing
      const perf = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      eventTracker.track("page_load", {
        path: location.pathname,
        load_time_ms: perf ? Math.round(perf.loadEventEnd - perf.startTime) : null,
        dom_interactive_ms: perf ? Math.round(perf.domInteractive - perf.startTime) : null,
        navigation_type: perf?.type ?? null,
      });
    }
    return () => eventTracker.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep context current when user or tenant changes
  useEffect(() => {
    const slugMatch = location.pathname.match(/^\/t\/([^/]+)/);
    const tenantId = slugMatch ? (tenantIdMap[slugMatch[1]] ?? null) : null;
    eventTracker.updateContext(user?.id ?? getUserIdFromCookie(), tenantId);
  }, [user?.id, tenantIdMap, location.pathname]);

  // Track page views on navigation + page visit duration
  const prevPathname = useRef<string>("");
  useEffect(() => {
    if (location.pathname !== prevPathname.current) {
      // Emit page_visit for the previous page with duration
      if (prevPathname.current) {
        const durationMs = Date.now() - pageEnteredAt.current;
        eventTracker.track("page_visit", {
          path: prevPathname.current,
          duration_ms: durationMs,
          duration_s: Math.round(durationMs / 1000),
        });
      }
      prevPathname.current = location.pathname;
      pageEnteredAt.current = Date.now();

      eventTracker.track("page_view", {
        path: location.pathname,
        search: location.search,
      });
    }
  }, [location.pathname, location.search]);

  // Global click listener — captures link clicks and button clicks
  const handleGlobalClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // Link click tracking
    const anchor = closest(target, "A") as HTMLAnchorElement | null;
    if (anchor) {
      const href = anchor.getAttribute("href") || "";
      const isExternal = anchor.hostname !== window.location.hostname;
      eventTracker.track("link_click", {
        href,
        text: (anchor.textContent || "").trim().slice(0, 120),
        is_external: isExternal,
        target: anchor.target || "_self",
        selector: elementSelector(anchor),
      }, { category: "navigation" });
      return; // Don't double-count as a button click if anchor wraps a button
    }

    // Button click tracking
    const button = closest(target, "BUTTON") as HTMLButtonElement | null;
    if (button) {
      eventTracker.track("button_click", {
        text: (button.textContent || "").trim().slice(0, 120),
        type: button.type || "button",
        disabled: button.disabled,
        selector: elementSelector(button),
        data_action: button.dataset.action || null,
      }, { category: "interaction" });
      return;
    }

    // Generic clickable element tracking (role="button", input[type="submit"], etc.)
    const clickable = target.closest('[role="button"], input[type="submit"], input[type="button"], [data-track-click]') as HTMLElement | null;
    if (clickable) {
      eventTracker.track("element_click", {
        text: (clickable.textContent || "").trim().slice(0, 120),
        role: clickable.getAttribute("role"),
        selector: elementSelector(clickable),
        data_action: (clickable as HTMLElement).dataset.action || null,
      }, { category: "interaction" });
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleGlobalClick, true);
    return () => document.removeEventListener("click", handleGlobalClick, true);
  }, [handleGlobalClick]);

  // Emit page_visit for the last page when the tab is hidden / user leaves
  useEffect(() => {
    const handlePageExit = () => {
      if (prevPathname.current) {
        const durationMs = Date.now() - pageEnteredAt.current;
        eventTracker.track("page_visit", {
          path: prevPathname.current,
          duration_ms: durationMs,
          duration_s: Math.round(durationMs / 1000),
        });
      }
    };
    window.addEventListener("beforeunload", handlePageExit);
    return () => window.removeEventListener("beforeunload", handlePageExit);
  }, []);

  return null;
}
